import React, { useState, useEffect } from "react";
import {
  Collapse,
  Row,
  Col,
  Button,
  Tooltip,
  Popconfirm,
  Alert,
  Modal,
} from "antd";
import { MinusOutlined, PlusOutlined, SendOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import {
  ProgressControl,
  progressIndicatorEnum,
  openNotification,
} from "@seims/shared";
import PersonalInformation from "./PersonalInformation";
import Qualification from "./Qualification";
import SupportingDocument from "./SupportingDocument";
import api from "../../services/nysService";
import util from "../../utils/functions/custom";
import { Messages, Notify } from "../../services/custom/Messages";
import logger, { recordError } from "../../utils/logService";
import { getClosingDate } from "./NysUtil";
import { nysIntakeMessage } from "../../utils/apiConstants";

const { confirm } = Modal;
const { Panel } = Collapse;

const steps = [
  {
    target: ".welcome",
    content: "This section shows the progress indicator of the application",
    disableBeacon: true,
  },
  {
    target: ".personal-info-background",
    content:
      "This section contains information about your personal information, please complete neccessary information and save.",
  },
  {
    target: ".qualification",
    content:
      "This section contains information about your Qualification, please complete neccessary information and save.",
  },
  {
    target: ".supporting-docs",
    content:
      "This section allow you to upload required documents for your application.",
  },
  {
    target: ".submit-withdraw",
    content:
      "You may submit or withdraw your application based on the status of your application.",
  },
];

const CandidateDetailsView = () => {
  const [demogrPogress, setdemogrPogress] = useState(
    progressIndicatorEnum.NOT_STARTED
  );
  const [qualifProgress, setqualifProgress] = useState(
    progressIndicatorEnum.NOT_STARTED
  );
  const [docsProgress, setdocsProgress] = useState(
    progressIndicatorEnum.NOT_STARTED
  );
  const [intake, setIntake] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [expanded, setExpanded] = useState(["1", "2", "3"]);
  const [demographics, setDemographics] = useState({});
  const [qualification, setQualification] = useState({});
  const [documents, setDocuments] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  const [disabledWithdraw, setDisableWithdraw] = useState(false);
  const [docHasUpdated, setDocHasUpdated] = useState(false);
  const [placementStatus, setPlacementStatus] = useState();
  // const [freezeSubmissions, setFreezeSubmissions] = useState(false);
  // const [closingDate, setClosingDate] = useState("");
  const [applicationsClosed, setApplicationsClosed] = useState(false);
  const [submissionAllowed, setSubmissionAllowed] = useState(true);
  const [nysIntake, setNysIntake] = useState(null);
  const [infoBar, setInfoBar] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  let path = `/nys/candidate/create`;

  useEffect(() => {
    getCondidate();
  }, [
    demogrPogress,
    qualifProgress,
    uploadProgress,
    docsProgress,
    readOnly,
    disabledWithdraw,
    docHasUpdated,
    nysIntake,
  ]);

  useEffect(() => {
    loadNysIntakeDetails();
  }, []);

  const handleDemographicProgress = (progressStatus) => {
    setdemogrPogress(progressStatus);
  };

  const handleQualifProgress = (progressStatus) => {
    setqualifProgress(progressStatus);
  };

  const handleDocsProgress = (progressStatus, progress) => {
    setdocsProgress(progressStatus);
    setUploadProgress(progress);
  };

  const handleSubmit = async () => {
    let msg;

    try {
      setSubmitted(true);
      const response = await api.submitProfile();
      setSubmitted(false);
      if (response && (response.status == 200 || response.status == 201)) {
        const { data } = response;

        setReadOnly(data.readOnly);
        msg = util.parseMessage(
          Messages.ADD_SUCCESS_APPLICATION,
          "Application"
        );
        // setLoading(false);

        openNotification("Candidate submission", msg, Notify.SUCCESS);
        navigate(path);
        return;
      }

      msg = util.parseMessage(Messages.ADD_FAILURE, "Application");
      openNotification("Candidate submission", msg, Notify.ERROR);
      // setLoading(false);
    } catch (error) {
      msg = util.parseMessage(Messages.ADD_FAILURE, "Application");
      openNotification("Candidate submission", msg, Notify.ERROR);
      recordError(error);
      // setLoading(false);
    }
    setSubmitted(false);
  };

  const handleWithdrawSubmit = async () => {
    let msg;

    try {
      const response = await api.withdraw(intake);

      if (response && (response.status == 200 || response.status == 204)) {
        setDisableWithdraw(true);
        msg = util.parseMessage(Messages.UPDATE, "Application");
        openNotification("Withdraw Application", msg, Notify.SUCCESS);
        return;
      }
    } catch (error) {
      msg = util.parseMessage(Messages.UPDATE_FAILURE, "Withdraw application");
      openNotification("Withdraw Application", msg, Notify.ERROR);
      // console.log("ERROR", error);
      recordError(error);
    }
  };

  const handleCancel = () => {
    navigate(path);
  };

  const showHideInfoBar = (title, content) => {
    if (!title || !content) {
      return;
    }

    confirm({
      title: title,
      content: content,
      okText: "Noted",
      cancelButtonProps: { style: { display: "none" } },
      onCancel() {},
      async onOk() {
        Modal.destroyAll();
      },
    });
  };

  const determineMessage = (hint) => {
    let _info = {
      subject: nysIntake?.subject,
      body: nysIntake?.message,
    };

    console.log("Applications closed: ", nysIntake);

    if (hint && hint !== "Intake-info") {
      _info = { ...nysIntakeMessage[hint] };
    } else if (
      hint &&
      hint === "Intake-info" &&
      applicationsClosed &&
      _info?.subject === "Applications Closed"
    ) {
      const _body = nysIntake?.closure
        ? `NYS application closing date has passed, last day for applications was ${nysIntake?.closure}.`
        : "NYS application closing date has passed.";

      _info.body = _body + " However, you can still update your details.";
    }

    setInfoBar(_info);
  };

  const loadNysIntakeDetails = async () => {
    const _nysIntake = await getClosingDate();
    const open = _nysIntake?.open || false;

    setNysIntake(_nysIntake);
    setApplicationsClosed(!open);
  };

  /*
  const loadInfoBar = async () => {
    //? Testing with the actual closing date
    //? const currentDate = moment("2024-03-31").add(1, 'days').format('YYYY-MM-DD');
    
    const currentDate = moment().format('YYYY-MM-DD HH:mm');

    let msg;

    try {
      const nysIntake = await getClosingDate();
      const open = nysIntake?.open || false;
      //const _title = nysApplication?.title || defaultTitle;


      const response = await api.getClosingDate(currentDate);

      if (response && (response.status == 200 || response.status == 201)) {
        const {data} = response;

        const midNightClosing = moment(data.endDate).endOf('day').format('YYYY-MM-DD HH:mm');
        const openingDate = moment(data.startDate).startOf('day').format('YYYY-MM-DD HH:mm');
        let closingTitle;
        let closingContent;
        console.log("Current date", currentDate, "Opening date", openingDate._i, "Closing date", midNightClosing);

        if (currentDate > midNightClosing) {
          closingTitle = "Submission Period Has Closed"
          closingContent = `NYS application closing date has passed, last day for applications was ${moment(midNightClosing).format("LLLL")}. However, you can still update your profile`;
          showHideInfoBar(closingTitle, closingContent);
          setFreezeSubmissions(true);
          setClosingDate(midNightClosing);
          setApplicationsClosed(true);
          return;
        }

        if (currentDate < openingDate && currentDate < midNightClosing) {
          closingTitle = "Applications not yet opened";
          closingContent = `NYS application period has not yet opened, applications will open on ${moment(openingDate).format("LLLL")}. However, you can still update your profile`;
          showHideInfoBar(closingTitle, closingContent);
          setFreezeSubmissions(true);
          setClosingDate(openingDate);
          setApplicationsClosed(true);
          return;
        }

      }
      else {
        msg = util.parseMessage(Messages.CLOSING_DATE, "Closing date");
        openNotification("Closing date", msg, Notify.ERROR);
      }
            
    } catch (error) {

      if (error && error?.response?.status == 404) {
        showHideInfoBar();
        setFreezeSubmissions(true);
        // If there's no closing date retrieved, so we currently show the current date because there's no record associated in the DB
        setClosingDate(currentDate);
        setApplicationsClosed(true);

        return;
      }

      msg = util.parseMessage(Messages.CLOSING_DATE, "Closing date");
      openNotification("Closing date", msg, Notify.ERROR);
      recordError(error);
    }
  };
*/

  const getCondidate = async () => {
    try {
      const response = await api.getCandidate();
      if (response && response.status === 200) {
        const { data } = response;
        console.log("INTAKE", data);
        setReadOnly(data.readOnly);
        setSubmissionAllowed(data.submissionAllowed);
        determineMessage(data?.messageHint);

        if (data.intake) {
          setIntake(data.intake);
        }

        if (
          ["Placed", "Withdrawn", "Rejected"].includes(
            data.placementStatus?.value
          )
        ) {
          setDisableWithdraw(true);
          // setDisableWithdraw(data.placementStatus?.value =="Withdrawn");
        }
        setPlacementStatus(data.placementStatus?.value);

        if (data.demographics != undefined) setDemographics(data.demographics);
        if (data.qualifications != undefined)
          setQualification(data.qualifications);
        if (data.supportingDocuments != undefined)
          setDocuments(data.supportingDocuments);
      }
    } catch (error) {
      // const err = Error(error);
      // console.log("TYPE OF", error);
      recordError(error);
    }
  };

  const canSubmit =
    demogrPogress === progressIndicatorEnum.COMPLETED &&
    qualifProgress === progressIndicatorEnum.COMPLETED &&
    docsProgress === progressIndicatorEnum.COMPLETED &&
    uploadProgress === 100 &&
    !applicationsClosed;

  const toolTipMessage = canSubmit
    ? "You may submit your profile"
    : "Disabled because of incomplete profile";

  // const toolTipMessageWithdrawal = (disabledWithdraw)
  //   ? "Disabled because you've already withrawn your application"
  //   : "You may withdraw your application";
  const toolTipMessageWithdrawal =
    placementStatus === "Placed"
      ? "Candidate has already been placed"
      : placementStatus === "Withdrawn"
      ? "Disabled because you've already withrawn your application"
      : "You may withdraw your application";

  return (
    <div className="nys-content">
      {/* <Joyride
        steps={steps}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        
      /> */}
      {infoBar && (
        <div className="nys-info-bar">
          <span>Please Note:</span>
          <p>{infoBar.body}</p>
        </div>
      )}

      <ProgressControl
        demogrPogress={demogrPogress}
        qualifProgress={qualifProgress}
        docsProgress={docsProgress}
        uploadProgress={uploadProgress}
        className="welcome"
      />
      <Collapse
        defaultActiveKey={expanded}
        expandIcon={({ isActive }) =>
          isActive ? <MinusOutlined /> : <PlusOutlined />
        }
        expandIconPosition="end"
      >
        <Panel
          header="Personal Information"
          key="1"
          className="personal-info-background"
        >
          <PersonalInformation
            onProgressUpdate={handleDemographicProgress}
            data={demographics}
            readOnly={readOnly}
          />
        </Panel>
        <Panel header="Qualifications" key="2" className="qualification">
          <Qualification
            onProgressUpdate={handleQualifProgress}
            data={qualification}
            readOnly={readOnly}
          />
        </Panel>
        <Panel
          header="Supporting Documents"
          key="3"
          className="supporting-docs"
        >
          <SupportingDocument
            onProgressUpdate={handleDocsProgress}
            data={documents}
            readOnly={readOnly}
            docHasUpdated={docHasUpdated}
            setDocHasUpdated={setDocHasUpdated}
          />
        </Panel>
      </Collapse>
      <br></br>
      <br />
      <Row>
        <Col xs={24} className="submit-withdraw">
          <Tooltip title={toolTipMessage} key="1tooltip">
            {submissionAllowed && (
              <Button
                className="right-align-button"
                key="2b"
                htmlType="submit"
                // type="primary"
                disabled={!canSubmit}
                onClick={handleSubmit}
                style={{
                  marginLeft: "40px",
                  backgroundColor: "#00b96b",
                  color: "#FFFFFF",
                }}
                icon={<SendOutlined />}
                loading={submitted}
              >
                Submit Application
              </Button>
            )}
          </Tooltip>
          <Tooltip title={toolTipMessageWithdrawal} key="2tooltip">
            {readOnly && !disabledWithdraw && (
              <Popconfirm
                title={
                  <p>
                    By clicking yes, you agree that you will no longer be
                    considered
                    <br />
                    for the NYS programme and cannot resubmit your application
                    for this application round,
                    <br /> proceed?
                  </p>
                }
                onConfirm={() => handleWithdrawSubmit()}
                onCancel={() => handleCancel()}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  key="3b"
                  type="danger"
                  htmlType="submit"
                  className="right-align-button"
                  style={{ marginLeft: "40px", color: "#f2f2f2" }}
                >
                  Withdraw Application
                </Button>
              </Popconfirm>
            )}
          </Tooltip>
        </Col>
      </Row>
      <br />
      <Row>
        {readOnly &&
          (placementStatus === "Submitted" ||
            placementStatus === "Withdrawn") && (
            <Col xs={24} md={24}>
              <Alert
                message="Your Application Status"
                description={<b>{placementStatus}</b>}
                type="info"
                showIcon
                closable
              />
            </Col>
          )}
      </Row>
    </div>
  );
};

export default CandidateDetailsView;
